import { Grid, Sheet, Typography } from '@mui/joy';
import { TrashSimple } from '@phosphor-icons/react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Button } from '~/components/ui/button';
import { Card, CardActions, CardContent } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { NewTemplateDialog } from '~/features/process-management/NewTemplateDialog';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ProcessService } from '~/services/api/processes/endpoints';
import { ProcessTemplate } from '~/services/api/processes/types';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';

enum ProcessClassification {
  CONFIDE = 1,
  TENANT,
  REPORT,
  PARTNER,
}

export const DeleteTemplateWarningDialog = ({
  templateID,
  compressed = false,
  onSuccessfulDelete = () => {},
}: {
  templateID: number;
  compressed?: boolean;
  onSuccessfulDelete?: () => void;
}) => {
  const { createToastNotification } = useToastNotificationContext();
  const deleteProcessTemplate = useWrappedMutation(
    ProcessService.endpoints.deleteProcessTemplate.useMutation(),
  );

  const deleteTemplate = async () => {
    await deleteProcessTemplate.mutateAsync(
      { id: templateID },
      {
        onSuccess: () => {
          createToastNotification('Template deleted.', 'success');
          onSuccessfulDelete();
        },
      },
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant='plain' color='danger' startDecorator={<TrashSimple />}>
          {compressed ? 'Remove' : 'Delete template'}
        </Button>
      </DialogTrigger>
      <DialogContent size='4xl'>
        <DialogTitle>
          <FormattedMessage defaultMessage='Delete Template' />
        </DialogTitle>
        <Typography>
          Would you like to delete this template? This action can&apos;t be
          undone.
        </Typography>
        <DialogFooter className='p-6'>
          <DialogClose asChild>
            <Button
              variant='outlined'
              color='neutral'
              disabled={deleteProcessTemplate.state.isLoading}
            >
              <FormattedMessage defaultMessage='Cancel' />
            </Button>
          </DialogClose>
          <Button
            variant='solid'
            color='danger'
            onClick={deleteTemplate}
            disabled={deleteProcessTemplate.state.isLoading}
          >
            <FormattedMessage defaultMessage='Delete' />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplateCard = ({ template }: { template: ProcessTemplate }) => {
  const navigate = useNavigate();
  const raisedShadowStyle =
    '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12);';

  return (
    <Card
      sx={{
        width: '263px',
        '&:hover': {
          boxShadow: raisedShadowStyle,
          '& > .actions': {
            visibility: 'visible',
          },
        },
      }}
    >
      <CardContent
        sx={{
          height: '108px',
        }}
      >
        <Typography fontSize={20}>{template.name}</Typography>
      </CardContent>

      <CardActions
        sx={{
          justifyContent: 'space-between',
          height: '60px',
        }}
        className='actions'
      >
        {template.classification !== ProcessClassification.CONFIDE &&
          template.classification !== ProcessClassification.PARTNER && (
            <>
              <DeleteTemplateWarningDialog
                templateID={template.id}
                compressed
              />
              <Button
                variant='plain'
                color='secondary'
                onClick={() =>
                  navigate(
                    `/customer/settings/templates/processes/${template.id}`,
                  )
                }
              >
                <FormattedMessage defaultMessage='Edit' />
              </Button>
            </>
          )}
      </CardActions>
    </Card>
  );
};

const TemplateGrid = ({ data }: { data: ProcessTemplate[] }) => {
  return (
    <Grid container spacing={2}>
      {data.map((template) => (
        <Grid key={template.name}>
          <TemplateCard template={template} />
        </Grid>
      ))}
    </Grid>
  );
};

export const ProcessTemplates = () => {
  const templateQuery = ProcessService.endpoints.processTemplates.useQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
    },
  );
  return (
    <Sheet
      sx={{
        width: '100%',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
        borderBottomRightRadius: '8px',
        borderBottomLeftRadius: '8px',
        borderLeft: '1px solid rgba(0, 0, 0, 0.16)',
        borderRight: '1px solid rgba(0, 0, 0, 0.16)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.16)',
        bgcolor: '#FFF',
        height: '100%',
      }}
    >
      <div className='flex flex-col gap-5 p-5 h-full'>
        <QueryResolver query={templateQuery}>
          {(data) => (
            <>
              <div className='flex flex-row justify-between items-center'>
                <Typography level='title-lg' fontWeight={400}>
                  Process Templates
                </Typography>
                {data && data.length > 0 && <NewTemplateDialog />}
              </div>
              {data && data.length > 0 ? (
                <TemplateGrid data={data} />
              ) : (
                <div className='flex flex-col justify-center pt-12 gap-8'>
                  <div className='flex flex-col justify-center items-center gap-2'>
                    <Typography level='title-lg' textAlign='center'>
                      <FormattedMessage defaultMessage='Design a custom process template' />
                    </Typography>
                    <Typography
                      level='body-sm'
                      textAlign='center'
                      sx={{
                        width: '50%',
                      }}
                    >
                      <FormattedMessage
                        defaultMessage='Create a unique process tailored to your compliance needs by
                    adding tasks, setting priorities, and defining roles'
                      />
                    </Typography>
                  </div>
                  <div className='flex justify-center'>
                    <NewTemplateDialog />
                  </div>
                </div>
              )}
            </>
          )}
        </QueryResolver>
      </div>
    </Sheet>
  );
};
